import * as React from 'react'
import { renderRichtext } from '../../lib/utils/renderRichtext'
import { useForm } from '../../lib/hooks/useForm'
import { useContactFormReasonOptions } from '../../lib/hooks/useContactFormReasonOptions'
import { TextInput } from '../../components/UI/TextInput'
import { Textarea } from '../../components/UI/Textarea'
import { Select } from '../../components/UI/Select'
import { Button } from '../../components/UI/Button'
import { Headline } from '../../components/UI/Headline'
import { Recaptcha } from '../../components/Recaptcha'
import { ContactPanelProps, IContactFormValues } from './types'
import {
  StyledBody,
  StyledContactInformations,
  StyledErrorMessage,
  StyledForm,
  StyledHeader,
  StyledInputGroup,
  StyledPanel,
  StyledPhoneInput,
  StyledSuccessMessage,
  StyledWrapper,
  TextInputHidden,
} from './styled'
import useEventTracking from '../../lib/hooks/tracking'
import getDomainFromEmail from '../../lib/utils/getDomainFromEmail'
import { sbEditable } from '@storyblok/storyblok-editable'
import { FC, useEffect, useState } from 'react'

import emailValidator from 'email-validator';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import posthog from 'posthog-js'
import 'react-phone-input-2/lib/style.css'
import { startsWith } from 'lodash'

export const forbiddenDomains = [
  'gmail',
  'aol',
  'yahoo',
  'hotmail',
  'outlook',
  '163',
  'icloud',
  'protonmail',
  'seznam',
  'orange',
  'qq',
  'drmail',
  'ymail',
  'teml',
  'gmx',
  't-online',
  'live',
  'libero',
  'comcast',
  'naver',
  'yopmail',
  'googlemail',
  'wanadoo',
  'tiscali',
  'yandex'
];

export const containsForbiddenDomain = (email) => {
  const domain = email.split('@')[1];
  const domainCheck = forbiddenDomains.some((forbiddenDomain) =>
    domain.includes(forbiddenDomain)
  );
  domainCheck && posthog.capture('validation_triggered', { domain: domain });
  return domainCheck;
};

export const ContactPanel: FC = ({ blok, className }: { blok: ContactPanelProps; className?: string }) => {
  const [selection, setSelection] = useState('')
  const [businessRole, setBusinessRole] = useState('')
  const emailRef = React.useRef(null);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const contactFormReasonOptions = useContactFormReasonOptions()
  const trackEvent = useEventTracking();


  const handleEmailChange = (event) => {
    const inputValue = emailRef.current.value;
    setEmail(inputValue);

    if (inputValue && (!emailValidator.validate(inputValue) || containsForbiddenDomain(inputValue))) {
      setEmailError(`Please enter your business email address. This form does not accept addresses from ${inputValue.split('@')[1]}.`);
    } else {
      setEmailError('');
    }
  };


  useEffect(() => {
    const inputValue = emailRef?.current?.value;
    if (inputValue && (!emailValidator.validate(inputValue) || containsForbiddenDomain(inputValue))) {
      setEmailError(`Please enter your business email address. This form does not accept addresses from ${inputValue.split('@')[1]}.`);
    }
  }, [emailRef?.current?.value]);


  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value);
  };



  const formRef = React.useRef(null)
  const { handleSubmit, status, statusCode, message } = useForm<IContactFormValues>({
    form: formRef.current,
    onSuccess: formData => {
      trackEvent('contact_form_submitted', {
        contact_company: formData.company,
        contact_email: getDomainFromEmail(formData.email),
        contact_first_name: formData.firstname,
        contact_last_name: formData.lastname,
        contact_message: formData.message,
        contact_reason: formData.reason,
        contact_role: formData.role,
        contact_phone: formData.phone,
      });
    },
  })

  const handleSubmitWithValidation = (event) => {
    event.preventDefault();
    if (emailError || phoneError) {
      return;
    }
    handleSubmit(event);
  }

  const theme = blok.theme || 'azure'

  return (
    <StyledWrapper className={theme ? `${className} theme-${theme}` : className} marginBlock={blok.marginBlock}  {...sbEditable(blok)}>
      <StyledPanel>
        {!!blok?.headline?.length && (
          <StyledHeader>
            <Headline is={blok.headlineElement} as={blok.headlineSize?.replace('is-', '')}>
              {blok.headline}
            </Headline>
          </StyledHeader>
        )}
        <StyledBody>
          {status === 'success' ? (
            <StyledSuccessMessage>{blok.confirmationText}</StyledSuccessMessage>
          ) : (
            <>
              <StyledForm
                action={`${process.env.GATSBY_PDFTOOLS_API_URL}/ContactForm`}
                onSubmit={handleSubmitWithValidation}
                method="POST"
                ref={formRef}
              >
                <StyledInputGroup>
                  <TextInput name="firstname" variant="light" label={blok.firstNameLabel} required />
                  <TextInput name="lastname" variant="light" label={blok.lastNameLabel} required />
                  <TextInput ref={emailRef} name="email" type="email" variant="light" label={blok.emailLabel} onBlur={handleEmailChange} required />
                </StyledInputGroup>
                <TextInputHidden>
                  <TextInput name="phone" variant="light" label={blok.phoneNumberLabel} value={phoneNumber} />
                </TextInputHidden>
                <StyledInputGroup>
                  <Select
                    name="reason"
                    value={selection}
                    onChange={e => setSelection(e.currentTarget.value)}
                    variant="light"
                    label={blok.productLabel}
                    required
                  >
                    {contactFormReasonOptions.map((b, index) => (
                      <option key={`key-${index}`} value={b.value}>
                        {b.name}
                      </option>
                    ))}
                  </Select>
                  <Select
                    name="role"
                    value={businessRole}
                    onChange={e => setBusinessRole(e.currentTarget.value)}
                    variant="light"
                    label={blok.businessRoleLabel}
                  >
                    {blok?.businessRoleOptions && blok?.businessRoleOptions.length && blok.businessRoleOptions.map((b, index) => (
                      <option key={`key-${index}`} value={b}>
                        {b}
                      </option>
                    ))}
                  </Select>
                  <StyledPhoneInput
                    placeholder={blok.phoneNumberLabel}
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                  />
                </StyledInputGroup>

                <Textarea name="message" variant="light" label={blok.messageLabel} required></Textarea>
                <Recaptcha />

                {status === 'error' && statusCode === 400 && (
                  <StyledErrorMessage>{blok.validationError}</StyledErrorMessage>
                )}
                {status === 'error' && statusCode === 503 && (
                  <StyledErrorMessage>{blok.processingFailure}</StyledErrorMessage>
                )}

                {emailError && <StyledErrorMessage>{emailError}</StyledErrorMessage>}

                {phoneError && (
                  <StyledErrorMessage>{phoneError}</StyledErrorMessage>
                )}

                <Button type="submit" disabled={status === 'loading' || !!emailError} $ignoreTheme>
                  {blok.sendButtonText}
                </Button>
              </StyledForm>
            </>
          )}
        </StyledBody>
      </StyledPanel>
    </StyledWrapper>
  )
}
